'use client'

import { useState, ChangeEvent } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { FileText, X, Upload } from 'lucide-react'
import type { SceneInput, SavedScene } from '@creanizer/types'
import { SceneFormStandard } from './SceneFormStandard'
import { SceneFormContinuation } from './SceneFormContinuation'
import { SceneFormOutline } from './SceneFormOutline'
import { SceneFormRewrite } from './SceneFormRewrite'

interface SceneChain {
  id: string
  name: string
  sceneIds: string[]
  createdAt: string
  updatedAt: string
}

interface SceneFormProps {
  input: SceneInput
  onChange: (input: SceneInput) => void
  scenes: SavedScene[]
  currentChainId: string
  continuationSourceType?: 'saved' | 'chapter'
  onContinuationSourceTypeChange?: (type: 'saved' | 'chapter') => void
  onChainCreate: (name: string, firstSceneId: string) => void
  onChainSelect?: (chainId: string) => void
  onChainReset?: () => void
  sceneChains?: SceneChain[]
}

export function SceneForm({ 
  input, 
  onChange, 
  scenes,
  currentChainId,
  continuationSourceType = 'saved',
  onContinuationSourceTypeChange,
  onChainCreate,
  onChainSelect,
  onChainReset,
  sceneChains = []
}: SceneFormProps) {
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; content: string }>>([])

  // ファイルアップロード処理
  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.type !== 'text/plain') {
      alert('テキストファイル(.txt)のみアップロード可能です')
      return
    }

    // 極端に大きいファイルのみ警告
    if (file.size > 100 * 1024 * 1024) {
      const confirm = window.confirm('ファイルサイズが100MBを超えています。処理に時間がかかる可能性がありますが、続行しますか？')
      if (!confirm) return
    }

    try {
      const text = await file.text()
      
      // ファイル情報を追加（IDはタイムスタンプ+ランダム文字列）
      const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
      const newFile = { id: fileId, name: file.name, content: text }
      const newFiles = [...uploadedFiles, newFile]
      
      setUploadedFiles(newFiles)
      
      // 全ファイルの内容を結合して uploadedContent に設定
      const combinedText = newFiles.map(f => `【参考資料: ${f.name}】\n${f.content}`).join('\n\n')
      
      console.log('【デバッグ】SceneForm onChange呼び出し（ファイルアップロード時）:', {
        uploadedContentLength: combinedText.length,
        uploadedContentPreview: combinedText.substring(0, 100),
        inputKeys: Object.keys(input),
        hasUploadedContent: !!combinedText
      })
      
      onChange({
        ...input,
        uploadedContent: combinedText
      })
    } catch (error) {
      console.error('ファイル読込エラー:', error)
      alert('ファイルの読み込みに失敗しました')
    }
    
    // ファイル選択をリセット
    if (e.target) {
      (e.target as HTMLInputElement).value = ''
    }
  }
  
  // ファイルを削除する関数
  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)
    
    // 残りのファイルの内容を結合
    const combinedText = newFiles.length > 0 
      ? newFiles.map(f => `【参考資料: ${f.name}】\n${f.content}`).join('\n\n')
      : undefined
    
    console.log('【デバッグ】SceneForm onChange呼び出し（ファイル削除時）:', {
      remainingFiles: newFiles.length,
      uploadedContent: combinedText ? '✅ あり' : '❌ なし',
      uploadedContentLength: combinedText?.length || 0
    })
    
    onChange({
      ...input,
      uploadedContent: combinedText
    })
  }

  const handleModeChange = (newMode: string) => {
    console.log('【デバッグ】モード変更:', {
      oldMode: input.mode,
      newMode: newMode,
      currentChainId: currentChainId,
      uploadedContent: input.uploadedContent ? '✅ あり' : '❌ なし',
      uploadedContentLength: input.uploadedContent?.length || 0
    })
    
    // 連続生成モード以外に切り替えた場合、チェーンをリセット
    if (newMode !== 'continuation' && onChainReset) {
      console.log('【デバッグ】連続生成モード以外へ変更 - チェーンリセット実行')
      onChainReset()
    }
    
    // モード変更時に基本フィールドを保持
    const baseFields = {
      genre: input.genre,
      customGenre: input.customGenre,
      worldContext: input.worldContext,
      uploadedContent: input.uploadedContent,  // 参考資料も保持する
      useProMode: input.useProMode,
      creativityLevel: input.creativityLevel,
      additionalSettings: input.additionalSettings,
      perspective: input.perspective,
      writingStyle: input.writingStyle,
      targetLength: input.targetLength
    }
    
    // モードに応じた初期値を設定
    switch (newMode) {
      case 'standard':
        onChange({
          ...baseFields,
          mode: 'standard',
          sceneType: 'description'
        })
        break
      case 'continuation':
        onChange({
          ...baseFields,
          mode: 'continuation',
          continuationType: 'direct'
        })
        break
      case 'outline':
        onChange({
          ...baseFields,
          mode: 'outline',
          outlineDetail: 'standard'
        })
        break
      case 'rewrite':
        onChange({
          ...baseFields,
          mode: 'rewrite'
        })
        break
    }
  }

  return (
    <div className="space-y-4 mt-4">
      {/* 参考資料アップロード（全モード共通） */}
      <div className="p-4 border rounded-lg bg-muted/30">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-base font-semibold">
            <Upload className="inline h-4 w-4 mr-1" />
            参考資料（オプション）
          </Label>
          {uploadedFiles.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {uploadedFiles.length}個のファイル読込済
            </span>
          )}
        </div>
        
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            前作の本文、世界観設定、プロット等の参考資料をアップロードできます。
            続編執筆や設定を踏まえた創作に活用されます。
          </p>
          
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start"
            onClick={() => document.getElementById('reference-file-input')?.click()}
          >
            <FileText className="mr-2 h-4 w-4" />
            テキストファイル（.txt）を選択
          </Button>
          <input
            id="reference-file-input"
            type="file"
            accept=".txt"
            className="hidden"
            onChange={handleFileUpload}
            multiple={false}
          />
          
          {/* 読み込んだファイルのリスト */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between bg-background p-2 rounded border">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm truncate">{file.name}</span>
                    <span className="text-xs text-muted-foreground">({file.content.length.toLocaleString()}文字)</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleFileRemove(file.id)}
                    className="p-1 ml-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="text-xs text-muted-foreground">
                合計: {uploadedFiles.reduce((sum, f) => sum + f.content.length, 0).toLocaleString()}文字
              </div>
            </div>
          )}
        </div>
      </div>

      <Tabs 
        value={input.mode || 'standard'} 
        onValueChange={handleModeChange}
      >
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="standard">標準</TabsTrigger>
          <TabsTrigger value="continuation">連続生成</TabsTrigger>
          <TabsTrigger value="outline">アウトライン</TabsTrigger>
          <TabsTrigger value="rewrite">リライト</TabsTrigger>
        </TabsList>

        <TabsContent value="standard" className="space-y-4">
          <SceneFormStandard
            input={input}
            onChange={onChange}
          />
        </TabsContent>

        <TabsContent value="continuation" className="space-y-4">
          <SceneFormContinuation
            input={input}
            onChange={(newInput) => {
              console.log('【デバッグ】SceneForm.onChange呼び出し:', {
                previousSceneId: newInput.previousSceneId,
                previousSceneTextLength: newInput.previousSceneText?.length || 0,
                previousSceneTextPreview: newInput.previousSceneText?.substring(0, 100) || 'なし'
              })
              onChange(newInput)
            }}
            scenes={scenes}
            currentChainId={currentChainId}
            sourceType={continuationSourceType}
            onSourceTypeChange={onContinuationSourceTypeChange}
            onChainCreate={onChainCreate}
            onChainSelect={onChainSelect}
            sceneChains={sceneChains}
          />
        </TabsContent>

        <TabsContent value="outline" className="space-y-4">
          <SceneFormOutline
            input={input}
            onChange={onChange}
          />
        </TabsContent>

        <TabsContent value="rewrite" className="space-y-4">
          <SceneFormRewrite
            input={input}
            onChange={onChange}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}